import { useState, useCallback, useRef } from 'react';

export interface UploadedFile {
    id: string;
    file: File;
    preview?: string;
    error?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
    'image/png', 'image/jpeg', 'image/gif', 'image/webp',
    'application/pdf', 'text/plain', 'text/csv',
    'application/json', 'text/markdown',
];

function validateFile(file: File): string | null {
    if (file.size > MAX_FILE_SIZE) {
        return `File too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`;
    }
    if (ALLOWED_TYPES.length > 0 && !ALLOWED_TYPES.includes(file.type)) {
        return `Unsupported file type: ${file.type || 'unknown'}`;
    }
    return null;
}

export function useFileUpload() {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const addFiles = useCallback((newFiles: FileList | File[]) => {
        const additions: UploadedFile[] = [];
        for (const file of Array.from(newFiles)) {
            const error = validateFile(file);
            const entry: UploadedFile = {
                id: crypto.randomUUID(),
                file,
                error: error ?? undefined,
            };
            if (!error && file.type.startsWith('image/')) {
                entry.preview = URL.createObjectURL(file);
            }
            additions.push(entry);
        }
        setFiles((prev) => [...prev, ...additions]);
    }, []);

    const removeFile = useCallback((id: string) => {
        setFiles((prev) => {
            const removed = prev.find((f) => f.id === id);
            if (removed?.preview) URL.revokeObjectURL(removed.preview);
            return prev.filter((f) => f.id !== id);
        });
    }, []);

    const clearFiles = useCallback(() => {
        setFiles((prev) => {
            prev.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview); });
            return [];
        });
    }, []);

    const openFilePicker = useCallback(() => {
        inputRef.current?.click();
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
        }
    }, [addFiles]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        const clipFiles = e.clipboardData.files;
        if (clipFiles.length > 0) {
            addFiles(clipFiles);
        }
    }, [addFiles]);

    return {
        files,
        isDragging,
        inputRef,
        addFiles,
        removeFile,
        clearFiles,
        openFilePicker,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        handlePaste,
    };
}
